import { useEffect, FC, useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Loader from '../../components/Loader/Loader';
import Content from '../../components/Content/Content';
import FormError from '../../components/FormError/FormError';
import { getPaymentOptions, getPreorder } from '../../redux/PreorderSlice/PreorderSelectors';
import { useAppDispatch } from '../../redux/Store';
import { fetchPaymentOptions, fetchPreorder } from '../../redux/PreorderSlice/PreorderThunks';
import { RemoteStatus } from '../../redux/SharedTypes';
import PreorderActions from '../../redux/PreorderSlice/PreorderActions';
import PreorderView from './PreorderView/PreorderView';
import { getErrorMessageOrDefault } from '../../helpers/errorMessageHelper/errorMessageHelper';

const messages = defineMessages({
	defaultErrorMessage: {
		id: 'payments.some_errors_occured',
		defaultMessage: 'Some errors occurred',
	},
});

const PreOrder: FC = () => {
	const { formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const { id = '' } = useParams<{ id?: string }>();
	const { status: preorderStatus, error: preorderError } = useSelector(getPreorder);
	const { status: paymentOptionStatus, error: paymentOptionsError } =
		useSelector(getPaymentOptions);

	const content = useMemo(() => {
		if ([paymentOptionStatus, preorderStatus].some((x) => x === RemoteStatus.InProgress))
			return <Loader />;

		if ([paymentOptionStatus, preorderStatus].some((x) => x === RemoteStatus.Error))
			return (
				<Content name={formatMessage(messages.defaultErrorMessage)} showBadge={false}>
					<FormError
						message={formatMessage(
							getErrorMessageOrDefault(preorderError || paymentOptionsError)
						)}
					/>
				</Content>
			);

		if ([paymentOptionStatus, preorderStatus].every((x) => x === RemoteStatus.Done))
			return <PreorderView />;

		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paymentOptionStatus, preorderStatus]);

	useEffect(() => {
		if (preorderStatus === RemoteStatus.None) dispatch(fetchPreorder(id));
	}, [dispatch, id, preorderStatus]);

	useEffect(() => {
		if (paymentOptionStatus === RemoteStatus.None) dispatch(fetchPaymentOptions(id));
	}, [dispatch, id, paymentOptionStatus]);

	useEffect(() => {
		return () => {
			dispatch(PreorderActions.reset());
		};
	}, [dispatch]);

	return content;
};

export default PreOrder;
